import { uniq } from "lodash";

// Possible hand ranks in descending strength order
export type HandRankName =
  | "Royal Flush"
  | "Straight Flush"
  | "Four of a Kind"
  | "Full House"
  | "Flush"
  | "Straight"
  | "Three of a Kind"
  | "Two Pair"
  | "One Pair"
  | "High Card";

export interface HandEvaluation {
  /** Name of the best made hand */
  rank: HandRankName;
  /** Numerical rank: 10 (best) -> 1 (worst)  */
  rankValue: number;
  /** Draws that are present but not yet made */
  draws: string[]; // e.g. ["Flush Draw", "Straight Draw"]
  /** Helpful human-readable description ready to display */
  description: string;
  /** Number of outs to improve */
  outs?: number;
  /** Approx equity percentage using rule of 2/4 */
  equityPct?: number;
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */

const RANK_ORDER = "23456789TJQKA"; // low -> high
const RANK_VALUE: Record<string, number> = Object.fromEntries(
  RANK_ORDER.split("").map((r, i) => [r, i + 2]) // 2 -> 14
);

interface CardInfo {
  rankChar: string;
  suit: string;
  value: number;
}

const parseCard = (card: string): CardInfo | null => {
  if (!card || card.length !== 2) return null;
  const [rankChar, suit] = card.toUpperCase().split("");
  if (!RANK_VALUE[rankChar]) return null;
  return { rankChar, suit: suit.toLowerCase(), value: RANK_VALUE[rankChar] };
};

const combinations = <T,>(arr: T[], k: number): T[][] => {
  const results: T[][] = [];
  const helper = (start: number, combo: T[]) => {
    if (combo.length === k) {
      results.push(combo.slice());
      return;
    }
    for (let i = start; i < arr.length; i++) {
      combo.push(arr[i]);
      helper(i + 1, combo);
      combo.pop();
    }
  };
  helper(0, []);
  return results;
};

/* -------------------------------------------------------------------------- */
/* Core 5-card hand evaluator (simplified but sufficient)                      */
/* -------------------------------------------------------------------------- */

interface FiveCardAnalysis {
  rank: HandRankName;
  rankValue: number;
  cards: CardInfo[]; // the 5-card combo
}

const analyseFiveCards = (cards: CardInfo[]): FiveCardAnalysis => {
  // Ensure exactly 5
  if (cards.length !== 5) throw new Error("analyseFiveCards requires 5 cards");
  const suits = cards.map((c) => c.suit);
  const values = cards.map((c) => c.value).sort((a, b) => b - a); // high -> low
  const counts: Record<number, number> = {};
  values.forEach((v) => {
    counts[v] = (counts[v] || 0) + 1;
  });
  const distinctValues = Object.keys(counts)
    .map(Number)
    .sort((a, b) => b - a);

  const isFlush = uniq(suits).length === 1;

  const isWheelStraight = [14, 5, 4, 3, 2].every((v) => values.includes(v));
  const isRegularStraight = distinctValues.length === 5 && distinctValues[0] - distinctValues[4] === 4;
  const isStraight = isRegularStraight || isWheelStraight;

  // Helper to map to rank value (10 best .. 1 worst)
  const rankOutcome = (name: HandRankName): FiveCardAnalysis => ({
    rank: name,
    rankValue:
      {
        "Royal Flush": 10,
        "Straight Flush": 9,
        "Four of a Kind": 8,
        "Full House": 7,
        Flush: 6,
        Straight: 5,
        "Three of a Kind": 4,
        "Two Pair": 3,
        "One Pair": 2,
        "High Card": 1,
      }[name],
    cards,
  });

  /* Evaluate hierarchy */
  if (isStraight && isFlush) {
    if (Math.max(...values) === 14 && Math.min(...values) === 10) {
      return rankOutcome("Royal Flush");
    }
    return rankOutcome("Straight Flush");
  }

  if (Object.values(counts).includes(4)) return rankOutcome("Four of a Kind");

  if (Object.values(counts).includes(3) && Object.values(counts).includes(2))
    return rankOutcome("Full House");

  if (isFlush) return rankOutcome("Flush");
  if (isStraight) return rankOutcome("Straight");
  if (Object.values(counts).includes(3)) return rankOutcome("Three of a Kind");
  if (Object.values(counts).filter((x) => x === 2).length === 2) return rankOutcome("Two Pair");
  if (Object.values(counts).includes(2)) return rankOutcome("One Pair");

  return rankOutcome("High Card");
};

/* -------------------------------------------------------------------------- */
/* Partial hand analysis ( <5 cards )                                         */
/* -------------------------------------------------------------------------- */

const analysePartial = (cards: CardInfo[]): FiveCardAnalysis => {
  const counts: Record<number, number> = {};
  cards.forEach((c) => (counts[c.value] = (counts[c.value] || 0) + 1));
  const countVals = Object.values(counts);

  const rankOutcome = (name: HandRankName): FiveCardAnalysis => ({
    rank: name,
    rankValue:
      {
        "Royal Flush": 10,
        "Straight Flush": 9,
        "Four of a Kind": 8,
        "Full House": 7,
        Flush: 6,
        Straight: 5,
        "Three of a Kind": 4,
        "Two Pair": 3,
        "One Pair": 2,
        "High Card": 1,
      }[name],
    cards,
  });

  // Four of kind
  if (countVals.includes(4)) return rankOutcome("Four of a Kind");
  if (countVals.includes(3)) {
    if (countVals.includes(2)) return rankOutcome("Full House");
    return rankOutcome("Three of a Kind");
  }
  const pairs = countVals.filter((v) => v === 2).length;
  if (pairs === 2) return rankOutcome("Two Pair");
  if (pairs === 1) return rankOutcome("One Pair");

  return rankOutcome("High Card");
};

/* -------------------------------------------------------------------------- */
/* Draw detection                                                             */
/* -------------------------------------------------------------------------- */

const hasFlushDraw = (cards: CardInfo[]): boolean => {
  const suitCounts: Record<string, number> = {};
  cards.forEach((c) => (suitCounts[c.suit] = (suitCounts[c.suit] || 0) + 1));
  return Object.values(suitCounts).some((cnt) => cnt === 4);
};

const hasStraightDraw = (values: number[]): boolean => {
  const unique = Array.from(new Set(values)).sort((a, b) => a - b);
  // Add wheel Ace as value 1 duplicate
  if (unique.includes(14)) unique.unshift(1);
  // Slide window of length 5 over possible straights; if we have 4 out of 5 then draw
  for (let i = 0; i < unique.length; i++) {
    const window = unique.slice(i, i + 5);
    if (window.length < 5) break;
    const missing = 5 - window.filter((v, idx, arr) => idx === 0 || v !== arr[idx - 1]).length;
    // Actually easier: count present values in potential straight sequence
    let present = 0;
    for (let v = window[0]; v < window[0] + 5; v++) {
      if (unique.includes(v)) present++;
    }
    if (present === 4) return true;
  }
  return false;
};

// Detect gut-shot (inside) straight draw – 4 outs
const hasGutshotDraw = (values: number[]): boolean => {
  const unique = Array.from(new Set(values)).sort((a, b) => a - b);
  if (unique.includes(14)) unique.unshift(1);
  for (let low = 1; low <= 10; low++) {
    const seq = [low, low + 1, low + 2, low + 3, low + 4];
    const missing = seq.filter((v) => !unique.includes(v));
    if (missing.length === 1 && seq.some((v) => unique.includes(v))) {
      return true;
    }
  }
  return false;
};

// Rough out count helper
const calculateOuts = (hasFlush: boolean, hasStraight: boolean): number => {
  if (hasFlush && hasStraight) return 15; // combo draw approx
  if (hasFlush) return 9;
  if (hasStraight) return 8;
  return 0;
};

/* -------------------------------------------------------------------------- */
/* Public evaluator                                                           */
/* -------------------------------------------------------------------------- */

export const evaluateTexasHoldemHand = (
  holeCards: string[],
  boardCards: string[]
): HandEvaluation => {
  const allCardsStrings = [...holeCards, ...boardCards].filter((c) => c && c.length === 2);
  const parsed = allCardsStrings.map(parseCard).filter(Boolean) as CardInfo[];
  if (parsed.length < 2) {
    return {
      rank: "High Card",
      rankValue: 1,
      draws: [],
      description: "No cards selected",
    };
  }

  let best: FiveCardAnalysis = { rank: "High Card", rankValue: 1, cards: parsed.slice(0, Math.min(5, parsed.length)) };

  if (parsed.length >= 5) {
    const combos = combinations(parsed, 5);
    combos.forEach((combo) => {
      const res = analyseFiveCards(combo);
      if (res.rankValue > best.rankValue) best = res;
    });
  } else {
    best = analysePartial(parsed);
  }

  // Draws only matter if board has <5 cards OR best hand weaker than Flush/Straight
  const draws: string[] = [];
  let outs = 0;
  if (boardCards.length > 0 && parsed.length < 7) {
    const flushD = best.rankValue < 6 && hasFlushDraw(parsed);
    const values = parsed.map((c) => c.value);
    const straightD = best.rankValue < 5 && hasStraightDraw(values);
    const gutshotD = best.rankValue < 5 && !straightD && hasGutshotDraw(values);

    if (flushD) draws.push("Flush Draw");
    if (straightD) draws.push("Straight Draw");
    if (gutshotD) draws.push("Gutshot Draw");

    // Pair / two-pair improvement draws
    let setDraw = false;
    let boatDraw = false;
    if (best.rank === "One Pair") {
      setDraw = true;
      draws.push("Set Draw");
      outs += 2; // two remaining cards to make trips
    }
    if (best.rank === "Two Pair") {
      boatDraw = true;
      draws.push("Full House Draw");
      outs += 4; // roughly 4 outs to make full house on next card
    }

    // Primary outs for flush/straight draws
    outs += calculateOuts(flushD, straightD);

    if (gutshotD) outs += 4;
  }

  // Build richer description names for pair-based hands
  const valueToWord: Record<number, string> = {
    2: "Twos",
    3: "Threes",
    4: "Fours",
    5: "Fives",
    6: "Sixes",
    7: "Sevens",
    8: "Eights",
    9: "Nines",
    10: "Tens",
    11: "Jacks",
    12: "Queens",
    13: "Kings",
    14: "Aces",
  };

  let rankLabel: string = best.rank;

  const getCounts = (cardsArr: CardInfo[]) => {
    const c: Record<number, number> = {};
    cardsArr.forEach((ci) => (c[ci.value] = (c[ci.value] || 0) + 1));
    return c;
  };

  const counts = getCounts(best.cards.length === 5 ? best.cards : parsed);
  const pairs = Object.keys(counts)
    .filter((v) => counts[Number(v)] === 2)
    .map(Number)
    .sort((a, b) => b - a);
  const trips = Object.keys(counts)
    .filter((v) => counts[Number(v)] === 3)
    .map(Number);
  const quads = Object.keys(counts)
    .filter((v) => counts[Number(v)] === 4)
    .map(Number);

  if (best.rank === "One Pair" && pairs.length === 1) {
    rankLabel = `Pair of ${valueToWord[pairs[0]]}`;
  } else if (best.rank === "Two Pair" && pairs.length >= 2) {
    rankLabel = `Two Pair (${valueToWord[pairs[0]]} & ${valueToWord[pairs[1]]})`;
  } else if (best.rank === "Three of a Kind" && trips.length === 1) {
    rankLabel = `Three ${valueToWord[trips[0]]}`;
  } else if (best.rank === "Four of a Kind" && quads.length === 1) {
    rankLabel = `Four ${valueToWord[quads[0]]}`;
  } else if (best.rank === "Full House" && trips.length === 1 && pairs.length >= 1) {
    rankLabel = `${valueToWord[trips[0]]} full of ${valueToWord[pairs[0]]}`;
  }

  const description = [rankLabel, ...draws].join(" | ");

  // Equity approximation
  let equityPct: number | undefined = undefined;
  if (outs > 0) {
    const boardCardsLeft = boardCards.length;
    const factor = boardCardsLeft === 3 ? 4 : boardCardsLeft === 4 ? 2 : 0; // flop 3 cards -> 4, turn 4 cards ->2
    equityPct = outs * factor;
    if (equityPct > 100) equityPct = 100;
  }

  return {
    rank: best.rank,
    rankValue: best.rankValue,
    draws,
    description,
    outs: outs || undefined,
    equityPct,
  };
}; 